/**
 * @fileoverview CategoriesPage - Category management page.
 *
 * Page for managing budget categories including adding new categories, ceasing
 * categories (setting end dates), reactivating ceased categories, and deleting
 * future-dated categories.
 *
 * @description This page component:
 * - Lists all categories with their status (active/ceased)
 * - Shows category start and end dates
 * - Allows adding new categories (with start date)
 * - Allows ceasing categories (sets end date, prevents new transactions)
 * - Allows reactivating ceased categories
 * - Allows deleting future-dated categories that have never been used
 * - Enforces role-based permissions
 *
 * Component composition:
 * - BackButton: Return to main page
 * - CategoryManagement: Main category CRUD component
 *
 * @module pages/CategoriesPage
 */
import CategoryManagement from '../components/CategoryManagement/CategoryManagement';
import BackButton from '../components/shared/BackButton';
import CeasedBudgetBanner from '../components/shared/CeasedBudgetBanner';
import { budgetAPI } from '../services/api';
import styles from './CategoriesPage.module.css';

/**
 * Category management page.
 *
 * @param {Object} props - Component props
 * @param {Object} props.categoryHook - Category hook with CRUD operations
 * @param {Object} props.budgetInfo - Budget metadata including start month
 * @param {string} props.userRole - Current user's role
 * @param {Function} props.onBack - Navigate back handler
 * @param {Function} props.onCategoryBalancesRefresh - Refresh category balances after changes
 */
function CategoriesPage({
  categoryHook,
  budgetInfo,
  userRole,
  onBack,
  onCategoryBalancesRefresh
}) {
  // Handler to restore budget (uncease)
  const handleRestoreBudget = async () => {
    try {
      await budgetAPI.unceaseBudget(budgetInfo.budgetKey);
      // Refresh the page to update budget info
      window.location.reload();
    } catch (error) {
      console.error('Failed to restore budget:', error);
      alert('Failed to restore budget. Please try again.');
    }
  };

  return (
    <div className={styles.container}>
      <BackButton onClick={onBack} />
      <h1 className={styles.pageTitle}>Categories</h1>

      {/* Ceased Budget Banner */}
      {budgetInfo?.ceaseDate && (
        <CeasedBudgetBanner
          ceaseDate={budgetInfo.ceaseDate}
          isOwner={userRole === 'owner'}
          onRestore={handleRestoreBudget}
        />
      )}

      <CategoryManagement
        categories={categoryHook.categories}
        onAddCategory={categoryHook.addCategory}
        onCeaseCategory={categoryHook.ceaseCategory}
        onReactivateCategory={categoryHook.reactivateCategory}
        onDeleteCategory={categoryHook.deleteCategory}
        onUpdateCategory={categoryHook.updateCategory}
        onReloadCategories={categoryHook.reloadCategories}
        userRole={userRole}
        budgetStartMonth={budgetInfo?.startMonth}
        showCategoryManagement={true}
        onToggle={() => {}}
        onCategoryBalancesRefresh={onCategoryBalancesRefresh}
        readOnly={budgetInfo?.ceaseDate !== null}
      />
    </div>
  );
}

export default CategoriesPage;
